import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { FIELD, LABEL, CARD, BTN_PRIMARY } from "@/lib/ui";
import { UserPlus, Trash2, KeyRound, Mail, ShieldCheck } from "lucide-react";
import { SubLinks } from "@/components/SubLinks";

const ROLES = [["owner", "Ιδιοκτήτης"], ["manager", "Διευθυντής"], ["hr", "HR"],
  ["concierge", "Concierge"], ["housekeeping", "Housekeeping"], ["fnb", "F&B"],
  ["maintenance", "Συντήρηση"], ["supplier", "Προμηθευτής"]];
const STAFF = ["concierge", "housekeeping", "fnb", "maintenance", "supplier"];

export default function Users() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isOwner = user?.role === "owner";
  const canManage = isOwner || user?.role === "manager" || user?.role === "hr";
  const canEditRow = (u) => isOwner || (user?.role === "manager" && u.role !== "owner") || (user?.role === "hr" && STAFF.includes(u.role));
  const [form, setForm] = useState({
    username: "", name: "", email: "", role: "housekeeping", password: "", employee_name: "", vendor_id: "", send_credentials: true,
  });
  const [pwFor, setPwFor] = useState({});

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["app-users"], queryFn: async () => (await api.get("/users")).data,
  });
  const { data: employees = [] } = useQuery({
    queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data,
  });
  const { data: vendors = [] } = useQuery({
    queryKey: ["vendors"], queryFn: async () => (await api.get("/vendors")).data,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["app-users"] });

  const create = useMutation({
    mutationFn: async () => (await api.post("/users", { ...form, employee_name: form.employee_name || null, vendor_id: form.vendor_id || null })).data,
    onSuccess: (d) => {
      refresh();
      setForm({ username: "", name: "", email: "", role: "housekeeping", password: "", employee_name: "", vendor_id: "", send_credentials: true });
      toast.success(d.credentials_emailed
        ? `Ο χρήστης ${d.username} δημιουργήθηκε — τα στοιχεία στάλθηκαν με email`
        : `Ο χρήστης ${d.username} δημιουργήθηκε — κωδικός: ${d.password}`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const update = useMutation({
    mutationFn: async ({ id, body }) => (await api.put(`/users/${id}`, body)).data,
    onSuccess: (d) => {
      refresh();
      setPwFor({});
      toast.success(d.credentials_emailed ? "Ενημερώθηκε — τα νέα στοιχεία στάλθηκαν με email" : "Ενημερώθηκε");
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const remove = useMutation({
    mutationFn: async ({ id, permanent }) => (await api.delete(`/users/${id}${permanent ? "?permanent=true" : ""}`)).data,
    onSuccess: (d) => { refresh(); toast.success(d.status === "erased" ? "Ο χρήστης διαγράφηκε οριστικά" : "Ο χρήστης μεταφέρθηκε στον Κάδο"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const askRemove = (u) => {
    if (!window.confirm(`Διαγραφή χρήστη «${u.username}»;`)) return;
    const permanent = window.confirm("ΟΡΙΣΤΙΚΗ διαγραφή (χωρίς Κάδο);\n\nOK = οριστικά · Άκυρο = στον Κάδο Ανακύκλωσης");
    remove.mutate({ id: u.id, permanent });
  };

  const allowedRoles = isOwner ? ROLES
    : user?.role === "manager" ? ROLES.filter(([r]) => r !== "owner")
    : ROLES.filter(([r]) => STAFF.includes(r));

  return (
    <div className="space-y-6" data-testid="users-page">
      <SubLinks links={[{ to: "/trash", label: "Κάδος Ανακύκλωσης" }, { to: "/audit-log", label: "Ιστορικό Αλλαγών" }, { to: "/team/permissions", label: "Δικαιώματα" }]} />
      <div>
        <p className="font-mono text-xs uppercase tracking-widest text-[#8A8577]">Πρόσβαση</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Χρήστες & Δικαιώματα</h1>
        <p className="text-[#6F6A60] text-sm mt-1">
          Η σύνδεση γίνεται με <strong>όνομα χρήστη και κωδικό</strong>. {isOwner
            ? "Ως ιδιοκτήτης δημιουργείτε λογαριασμούς σε όλα τα επίπεδα."
            : "Ως διευθυντής δημιουργείτε και επεξεργάζεστε λογαριασμούς υπαλλήλων."}
        </p>
      </div>

      {canManage && (
        <div className={`${CARD} p-5`} data-testid="user-form">
          <h2 className="font-medium text-[#45423C] flex items-center gap-2 mb-4">
            <UserPlus className="h-4 w-4 text-[#C86235]" /> Νέος χρήστης
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className={LABEL}>Όνομα χρήστη</label>
              <input data-testid="user-username" className={FIELD} value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="giorgos" />
            </div>
            <div>
              <label className={LABEL}>Ονοματεπώνυμο</label>
              <input data-testid="user-name" className={FIELD} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className={LABEL}>Email (για αποστολή)</label>
              <input data-testid="user-email" className={FIELD} value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <label className={LABEL}>Ρόλος</label>
              <select data-testid="user-role" className={FIELD} value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {allowedRoles.map(([r, l]) => <option key={r} value={r}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Σύνδεση με υπάλληλο</label>
              <select data-testid="user-employee" className={FIELD} value={form.employee_name}
                onChange={(e) => setForm({ ...form, employee_name: e.target.value })}>
                <option value="">— κανένας —</option>
                {employees.map((e) => <option key={e.id} value={e.name}>{e.name}</option>)}
              </select>
            </div>
            {form.role === "supplier" && (
              <div>
                <label className={LABEL}>Σύνδεση με προμηθευτή</label>
                <select data-testid="user-vendor" className={FIELD} value={form.vendor_id}
                  onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}>
                  <option value="">— επιλέξτε —</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className={LABEL}>Κωδικός (κενό = αυτόματος)</label>
              <input data-testid="user-password" className={FIELD} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
            <label className="inline-flex items-center gap-2 text-sm text-[#6F6A60]">
              <input data-testid="user-send-credentials" type="checkbox" checked={form.send_credentials}
                onChange={(e) => setForm({ ...form, send_credentials: e.target.checked })} />
              <Mail className="h-3.5 w-3.5" /> Αποστολή στοιχείων με email
            </label>
            <button data-testid="user-create" onClick={() => create.mutate()} disabled={create.isPending || !form.username}
              className={BTN_PRIMARY}>
              <UserPlus className="h-4 w-4" /> Δημιουργία
            </button>
          </div>
        </div>
      )}

      <div className={`${CARD} overflow-hidden`}>
        <div className="px-5 py-4 border-b border-[#E3DED4] flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-[#6E7A63]" />
          <h2 className="font-medium text-[#45423C]">Λογαριασμοί ({users.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                <th className="px-4 py-3">Όνομα χρήστη</th>
                <th className="px-4 py-3">Ονοματεπώνυμο</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Ρόλος</th>
                <th className="px-4 py-3">Υπάλληλος</th>
                {canManage && <th className="px-4 py-3">Νέος κωδικός</th>}
                {isOwner && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#6F6A60]">Φόρτωση…</td></tr>}
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.username}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                  <td className="px-4 py-2 font-semibold text-[#45423C]">{u.username}</td>
                  <td className="px-4 py-2">{u.name}</td>
                  <td className="px-4 py-2 text-[#6F6A60]">{u.email || "—"}</td>
                  <td className="px-4 py-2">
                    {canEditRow(u) ? (
                      <select data-testid={`user-role-${u.username}`} value={u.role}
                        onChange={(e) => update.mutate({ id: u.id, body: { role: e.target.value } })}
                        className="rounded-lg border border-[#E3DED4] bg-white px-2 py-1 text-xs">
                        {allowedRoles.map(([r, l]) => <option key={r} value={r}>{l}</option>)}
                      </select>
                    ) : (ROLES.find(([r]) => r === u.role) || [, u.role])[1]}
                  </td>
                  <td className="px-4 py-2 text-[#6F6A60]">{u.employee_name || "—"}</td>
                  {canManage && (
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <input data-testid={`user-newpw-${u.username}`} placeholder="νέος κωδικός"
                          value={pwFor[u.id] || ""} onChange={(e) => setPwFor({ ...pwFor, [u.id]: e.target.value })}
                          className="w-36 rounded-lg border border-[#E3DED4] px-2 py-1 text-xs outline-none focus:border-[#B9AE9A]" />
                        <button data-testid={`user-setpw-${u.username}`}
                          onClick={() => update.mutate({ id: u.id, body: { password: pwFor[u.id], send_credentials: true } })}
                          disabled={!(pwFor[u.id] || "").trim()}
                          title="Αλλαγή κωδικού & αποστολή email"
                          className="p-1.5 rounded-lg text-[#6E7A63] hover:bg-[#EEF0EA] disabled:opacity-40">
                          <KeyRound className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  )}
                  {isOwner && (
                    <td className="px-4 py-2 text-right">
                      <button data-testid={`user-delete-${u.username}`} onClick={() => askRemove(u)}
                        className="p-1.5 rounded-lg text-[#991B1B] hover:bg-[#FEF2F2]">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
